/*
  articleFeaturedProducts:
  This JavaScript module is designed to manage a carousel of featured products within an article. The module uses Swiper to create a responsive and interactive slider that adjusts based on the screen width and device type.

  - Properties:
    - swiper: Holds the instance of the Swiper object.
    - featuredProducts: An array to store the products fetched from the server.
    - swiperContainer: A reference to the DOM element where the Swiper will be initialized.

  - Methods:
    - init(): Asynchronously fetches products, caches DOM nodes, and builds the Swiper instance.
    - cacheNodes(): Caches the swiper container element for quick access.
    - buildSwiper(): Initializes the Swiper instance with specified configurations, including responsive breakpoints and navigation controls.
    - fetchProducts(): Fetches a collection of products to be featured, typically bestsellers or a specific category.
    - selectedProducts(): A getter to retrieve the fetched products for external use.

  This module enhances the article's interactivity by showcasing relevant products, potentially increasing engagement and conversion rates.

  articleFeaturedArticles:
  This module manages a carousel of featured articles. Similar to `articleFeaturedProducts`, it employs Swiper for fluid navigation between articles. It's particularly useful for blogs or news sections that want to highlight specific content.

  - Properties:
    - articles: An array to store articles fetched from the server.
    - swiperTabsInstance: Instance of Swiper controlling the tabs for article navigation.

  - Methods:
    - initialSetup(): Fetches a set of articles based on a specific blog handle and stores them.
    - buildSwiper(): Configures and initializes the Swiper instance for navigating through articles.
    - selectedArticles(): Filters and returns articles excluding the current one based on URL comparison.
    - getDate(dateString): Converts a string date into a formatted string (Month Day, Year).
*/

function articleFeaturedProducts() {
  return {
    swiper: null,
    featuredProducts: [],
    swiperContainer: null,

    async init() {
      await this.fetchProducts();
      this.cacheNodes();
      this.buildSwiper();
    },

    cacheNodes() {
      this.swiperContainer = document.querySelector('.article__featured-products-carousel');
    },

    buildSwiper() {
      if (this.swiper || !this.swiperContainer) {
        return;
      }
      this.swiper = new Swiper(this.swiperContainer, {
        slidesPerView: 4,
        spaceBetween: 32,
        autoHeight: false,
        mousewheel: {
          forceToAxis: true,
        },
        freeMode: {
          momentum: false,
        },
        breakpoints: {
          // when window width is >= 320px
          320: {
            slidesPerView: 1,
            spaceBetween: 20,
          },
          // when window width is >= 640px
          640: {
            slidesPerView: 2,
          },
          1024: {
            slidesPerView: 4,
            spaceBetween: 32,
          },
        },
        navigation: {
          nextEl: this.swiperContainer.querySelector('.article__featured-products-carousel--next'),
          prevEl: this.swiperContainer.querySelector('.article__featured-products-carousel--prev'),
          disabledClass: 'swiper-button-hidden',
        },
      });
    },

    async fetchProducts() {
      const { products } = await fetchCollection('bestsellers', 10);
      this.featuredProducts = products;
    },

    get selectedProducts() {
      return this.featuredProducts;
    },
  };
}

document.addEventListener('alpine:init', () => {
  Alpine.data('articleFeaturedProducts', articleFeaturedProducts);
});

function articleFeaturedArticles() {
  return {
    articles: [],

    initialSetup: async function () {
      const { articles } = await fetchBlogs(20, this.blogHandle);
      this.articles = articles;
    },

    buildSwiper() {
      this.swiperTabsInstance = new Swiper(document.querySelector('.article__featured-articles-carousel'), {
        freeMode: {
          momentum: false,
        },
        mousewheel: {
          forceToAxis: true,
        },
        breakpoints: {
          // when window width is >= 320px
          320: {
            slidesPerView: 1,
            spaceBetween: 20,
          },
          // when window width is >= 640px
          640: {
            slidesPerView: 2,
          },
          1024: {
            slidesPerView: 4,
            spaceBetween: 32,
          },
        },
      });
    },

    get selectedArticles() {
      return this.articles.filter((article) => article.articleHandle !== window.location.pathname.split('/')[3]);
    },

    getDate(dateString) {
      const date = new Date(dateString);

      const month = date.toLocaleString('default', { month: 'long' });
      const year = date.toLocaleString('default', { year: 'numeric' });
      const day = date.toLocaleString('default', { day: 'numeric' });

      return `${month} ${day}, ${year}`;
    },

    init: async function () {
      this.blogHandle = window.location.pathname.split('/')[2];

      await this.initialSetup();
      this.buildSwiper();
    },
  };
}

document.addEventListener('alpine:init', () => {
  Alpine.data('articleFeaturedArticles', articleFeaturedArticles);
});
